import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { NodeType, WorkflowNode } from '../../shared-services/pipeline-workflow.model';
import { PipelineNodeBadgeComponent } from '../pipeline-node-badge/pipeline-node-badge.component';

type PipelineFlowNodeWidgetData = {
  node?: WorkflowNode;
  index?: number;
  isActive?: boolean;
  displayType?: NodeType;
  badgeLabel?: string;
  label?: string;
  description?: string;
  icon?: string;
  metaChips?: string[];
  statusLabel?: string;
  statusClass?: string;
  trailingAction?: {
    visible?: boolean;
    icon?: string;
    tooltip?: string;
    cssClass?: string;
    onClick?: (nodeId: string) => void;
  };
  config?: any;
  onNodeClick?: (nodeId: string) => void;
  onInsertBefore?: (nodeId: string) => void;
};

@Component({
  selector: 'app-pipeline-flow-node',
  standalone: true,
  imports: [CommonModule, CdkDragHandle, PipelineNodeBadgeComponent],
  templateUrl: './pipeline-flow-node.component.html',
  styleUrl: './pipeline-flow-node.component.scss',
})
export class PipelineFlowNodeComponent {
  @Input() node!: WorkflowNode;
  @Input() index: number = 0;
  @Input() isActive: boolean = false;
  @Input() displayType?: NodeType;
  @Input() badgeLabel: string | null = null;
  @Input() label: string | null = null;
  @Input() description: string | null = null;
  @Input() icon: string | null = null;
  @Input() metaChips: string[] | null = null;
  @Input() statusLabel: string | null = null;
  @Input() statusClass: string | null = null;
  @Input() trailingAction: {
    visible?: boolean;
    icon?: string;
    tooltip?: string;
    cssClass?: string;
    onClick?: (nodeId: string) => void;
  } | null = null;
  @Input() config: any = null;
  @Input() set data(value: PipelineFlowNodeWidgetData | null | undefined) {
    if (!value) return;
    if (value.node) this.node = value.node;
    this.index = value.index ?? this.index;
    this.isActive = value.isActive ?? this.isActive;
    this.displayType = value.displayType ?? this.displayType;
    this.badgeLabel = value.badgeLabel ?? this.badgeLabel;
    this.label = value.label ?? this.label;
    this.description = value.description ?? this.description;
    this.icon = value.icon ?? this.icon;
    this.metaChips = value.metaChips ?? this.metaChips;
    this.statusLabel = value.statusLabel ?? this.statusLabel;
    this.statusClass = value.statusClass ?? this.statusClass;
    this.trailingAction = value.trailingAction ?? this.trailingAction;
    this.config = value.config ?? this.config;
    this.onNodeClick = value.onNodeClick;
    this.onInsertBefore = value.onInsertBefore;
  }

  @Output() nodeClick = new EventEmitter<string>();
  @Output() insertBefore = new EventEmitter<string>();

  private onNodeClick?: (nodeId: string) => void;
  private onInsertBefore?: (nodeId: string) => void;

  get effectiveType(): NodeType {
    return this.displayType ?? this.node?.type ?? 'AUTO_APPROVE';
  }

  get accentClass(): string {
    return `accent-${this.effectiveType.toLowerCase().replace('_', '-')}`;
  }

  get showTopConnector(): boolean {
    return this.readFlag(this.config?.showTopConnector, true);
  }

  get showInsertBeforeButton(): boolean {
    return this.readFlag(this.config?.showInsertBeforeButton, true);
  }

  get insertBeforeTooltip(): string {
    return this.readText(this.config?.insertBeforeTooltip, 'Insert node before');
  }

  get showAccentStripe(): boolean {
    return this.readFlag(this.config?.showAccentStripe, true);
  }

  get showDragHandle(): boolean {
    return this.readFlag(this.config?.showDragHandle, true);
  }

  get showIndex(): boolean {
    return this.readFlag(this.config?.showIndex, true);
  }

  get showBadge(): boolean {
    return this.readFlag(this.config?.showBadge, true);
  }

  get showStatusChip(): boolean {
    return this.readFlag(this.config?.showStatusChip, true);
  }

  get showLabel(): boolean {
    return this.readFlag(this.config?.showLabel, true);
  }

  get showDescription(): boolean {
    return this.readFlag(this.config?.showDescription, false);
  }

  get showMetaChips(): boolean {
    return this.readFlag(this.config?.showMetaChips, true);
  }

  get showChevron(): boolean {
    return this.readFlag(this.config?.showChevron, true);
  }

  get hideInsertButtonWhenActive(): boolean {
    return this.readFlag(this.config?.hideInsertButtonWhenActive, false);
  }

  get allowClickSelection(): boolean {
    return this.readFlag(this.config?.allowClickSelection, true);
  }

  get isCatalogVariant(): boolean {
    return this.readText(this.config?.cardVariant, '') === 'catalog';
  }

  get catalogIcon(): string {
    return this.readText(this.icon, '');
  }

  get showTrailingAction(): boolean {
    return this.readFlag(this.trailingAction?.visible, false);
  }

  get trailingActionTooltip(): string {
    return this.readText(this.trailingAction?.tooltip, 'Action');
  }

  get trailingActionIcon(): string {
    return this.readText(this.trailingAction?.icon, 'remove').toLowerCase();
  }

  get trailingActionClass(): string {
    return this.readText(this.trailingAction?.cssClass, '');
  }

  get missingMetaChipValues(): string[] {
    const values = this.config?.missingMetaChipValues;
    return Array.isArray(values)
      ? values.map((value) => String(value || '').trim()).filter(Boolean)
      : ['No approvers'];
  }

  get displayLabel(): string {
    return this.label?.trim() || this.node?.label || '';
  }

  get displayDescription(): string {
    return this.description?.trim() || '';
  }

  get statusChip(): { label: string; cls: string } {
    const customLabel = this.statusLabel?.trim();
    const customClass = this.statusClass?.trim();
    if (customLabel) {
      return { label: customLabel, cls: customClass || 'chip-valid' };
    }
    if (!this.node.isValid && this.node.hasWarning) return { label: 'Warning', cls: 'chip-warning' };
    if (!this.node.isValid) return { label: 'Incomplete', cls: 'chip-incomplete' };
    return { label: 'Valid', cls: 'chip-valid' };
  }

  get effectiveMetaChips(): string[] {
    if (this.metaChips?.length) {
      return this.metaChips.filter((chip) => chip?.trim().length);
    }

    const chips: string[] = [];
    if (this.node?.triggerConditions?.length) {
      chips.push(`${this.node.triggerConditions.length} trigger${this.node.triggerConditions.length > 1 ? 's' : ''}`);
    }
    if (this.node?.approvers?.length) {
      chips.push(`${this.node.approvers.length} approver${this.node.approvers.length > 1 ? 's' : ''}`);
    }
    if (!this.node?.isValid && !this.node?.approvers?.length && this.node?.type !== 'AUTO_APPROVE') {
      chips.push('No approvers');
    }
    return chips;
  }

  isMetaChipMissing(chip: string): boolean {
    return this.missingMetaChipValues.includes(chip);
  }

  handleNodeClick(): void {
    if (!this.allowClickSelection) {
      return;
    }
    this.nodeClick.emit(this.node.id);
    this.onNodeClick?.(this.node.id);
  }

  handleInsertBefore(event: Event): void {
    this.insertBefore.emit(this.node.id);
    this.onInsertBefore?.(this.node.id);
    event.stopPropagation();
  }

  handleTrailingAction(event: Event): void {
    this.trailingAction?.onClick?.(this.node.id);
    event.stopPropagation();
  }

  private readFlag(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  private readText(value: unknown, fallback: string): string {
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
  }
}
